/**
 * lazyWithRetry — wraps React.lazy with automatic retry on ChunkLoadError.
 *
 * Per ~/Documents/Claude/Brain/brain/12d4dabd.../global_rules.md §5 — Runtime Resilience
 * Per ~/Documents/Claude/Brain/knowledge/firebase_standards
 *
 * When a deploy ships, browsers may have stale chunk references. Without this wrapper,
 * users see a blank screen until they hard-refresh. With it, we silently re-fetch the chunk.
 */

import { lazy, type ComponentType } from "react";

// Route components take no props, so props are typed `any` here — the wrapper only cares about
// the dynamic-import retry behavior, not the component's prop shape.
type DynamicImport = () => Promise<{ default: ComponentType<any> }>;

export function lazyWithRetry(
  importer: DynamicImport,
  retries = 3,
  delayMs = 500,
) {
  return lazy(async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await importer();
      } catch (error) {
        lastError = error;
        /*
         * DRB-57: this used to match ONLY /ChunkLoadError|Loading chunk \d+ failed/, which are
         * WEBPACK's messages. This is a Vite app, so the test could never be true and the retry
         * loop was dead code: any failed import broke straight out and reloaded with no retry at
         * all. Vite/Rollup throw a different string per browser, so match all three.
         */
        const msg = error instanceof Error ? error.message : String(error);
        const isChunkError =
          /ChunkLoadError|Loading chunk \d+ failed/i.test(msg) || // webpack (kept: harmless)
          /Failed to fetch dynamically imported module/i.test(msg) || // Chrome / Edge
          /error loading dynamically imported module/i.test(msg) || // Firefox
          /Importing a module script failed/i.test(msg); // Safari
        if (!isChunkError || attempt === retries) break;
        await new Promise((r) => setTimeout(r, delayMs * (attempt + 1)));
      }
    }
    // Last resort — hard reload to pick up fresh manifest
    if (typeof window !== "undefined") {
      console.warn(
        "[lazyWithRetry] All retries exhausted. Forcing reload.",
        lastError,
      );
      window.location.reload();
    }
    throw lastError;
  });
}
